import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attending, setAttending] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [id, isAuthenticated]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/events/${id}`);
      const eventData = res.data.data;
      setEvent(eventData);
      if (isAuthenticated && user) {
        // Check if current user is in attendees array
        const isAttending = eventData.attendees?.some(
          (attendee) => {
            const attendeeId = attendee._id || attendee;
            const userId = user.id || user._id;
            return attendeeId?.toString() === userId?.toString();
          }
        );
        setAttending(isAttending || false);
      }
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Failed to load event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleAttend = async () => {
    if (!isAuthenticated) {
      toast.info('Please login to register for events');
      navigate('/login');
      return;
    }

    try {
      await api.post(`/events/${id}/attend`);
      toast.success('Successfully registered for event!');
      loadEvent();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return null;
  }

  const getCategoryColor = (category) => {
    const colors = {
      cultural: '#f97316',
      religious: '#059669',
      social: '#7c3aed',
      educational: '#dc2626',
      other: '#6b7280',
    };
    return colors[category] || '#6b7280';
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Button
          component={Link}
          to="/events"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 4 }}
        >
          Back to Events
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 3 }}>
              {event.image && (
                <Box
                  component="img"
                  src={event.image}
                  alt={event.title}
                  sx={{ width: '100%', maxHeight: 400, objectFit: 'cover' }}
                />
              )}
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Chip
                    label={event.category}
                    sx={{
                      backgroundColor: getCategoryColor(event.category),
                      color: '#fff',
                      textTransform: 'capitalize',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  {event.title}
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Event Details
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <EventIcon sx={{ color: '#f97316', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1">
                      {new Date(event.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOnIcon sx={{ color: '#f97316', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">{event.location}</Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ color: '#f97316', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Organizer
                    </Typography>
                    <Typography variant="body1">
                      {event.organizer?.name || 'GBCN'}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAttend}
                  disabled={attending}
                  sx={{
                    backgroundColor: '#f97316',
                    '&:hover': { backgroundColor: '#ea580c' },
                  }}
                >
                  {attending ? 'Already Registered' : 'Register for Event'}
                </Button>
              </CardContent>
            </Card>

            {event.attendees && event.attendees.length > 0 && (
              <Card sx={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Attendees ({event.attendees.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {event.attendees.slice(0, 10).map((attendee) => (
                      <Box key={attendee._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#f97316' }}>
                          {attendee.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{attendee.name}</Typography>
                      </Box>
                    ))}
                    {event.attendees.length > 10 && (
                      <Typography variant="body2" color="text.secondary">
                        +{event.attendees.length - 10} more
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default EventDetails;

